import { cn } from '@/lib/utils'
import { Button } from './button'

/** Table chrome per design.md §6 — header treatment, hover rows, pagination, totals slot. */

export function Table({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
      <table className={cn('w-full text-sm', className)} {...props} />
    </div>
  )
}

export function THead({ children }: { children: React.ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
        {children}
      </tr>
    </thead>
  )
}

export function Th({ className, right, ...props }: React.ThHTMLAttributes<HTMLTableCellElement> & { right?: boolean }) {
  return <th className={cn('px-4 py-3 font-medium', right && 'text-right', className)} {...props} />
}

export type SortDir = 'asc' | 'desc'

/**
 * Sortable header cell. Sorting is applied server-side — sorting only the 50 rows
 * of the current page would silently mislead on a paginated table.
 */
export function SortableTh({
  label,
  field,
  active,
  dir,
  onSort,
  right,
}: {
  label: string
  field: string
  active: boolean
  dir: SortDir
  onSort: (field: string, dir: SortDir) => void
  right?: boolean
}) {
  const next: SortDir = active && dir === 'asc' ? 'desc' : 'asc'
  return (
    <th
      className={cn('px-4 py-3 font-medium', right && 'text-right')}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field, next)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground',
          active && 'text-foreground',
        )}
      >
        {label}
        <span aria-hidden className={cn('text-[0.65rem]', !active && 'opacity-30')}>
          {active && dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  )
}

export function Tr({ className, clickable, ...props }: React.HTMLAttributes<HTMLTableRowElement> & { clickable?: boolean }) {
  return (
    <tr
      className={cn('border-b border-border last:border-b-0', clickable && 'cursor-pointer hover:bg-muted/50', className)}
      {...props}
    />
  )
}

export function Td({ className, right, ...props }: React.TdHTMLAttributes<HTMLTableCellElement> & { right?: boolean }) {
  return <td className={cn('px-4 py-3', right && 'text-right', className)} {...props} />
}

export function TotalsRow({ children }: { children: React.ReactNode }) {
  return <tr className="bg-muted font-semibold">{children}</tr>
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number
  pageSize: number
  total: number
  onPage: (p: number) => void
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  if (pages <= 1) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(total, page * pageSize)
  return (
    <div className="flex items-center justify-between px-1 pt-3">
      <span className="text-xs text-muted-foreground" data-numeric>
        Showing {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <span className="text-xs text-muted-foreground" data-numeric>
          Page {page} of {pages}
        </span>
        <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  )
}

export function SkeletonRows({ cols, rows = 5 }: { cols: number; rows?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, i) => (
        <Tr key={i}>
          {Array.from({ length: cols }, (_, j) => (
            <Td key={j}>
              <div className="h-4 animate-pulse rounded bg-muted" />
            </Td>
          ))}
        </Tr>
      ))}
    </tbody>
  )
}
